import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, XCircle, Clock, Sparkles, Inbox, BarChart3, History,
  Users, TrendingUp, AlertCircle, Mail,
} from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-50 text-slate-600 border-slate-200",
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
function fmtDateTime(s) {
  if (!s) return "—";
  try {
    const dt = new Date(s);
    return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}, ${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
  } catch { return s; }
}

function StatusPill({ status }) {
  return (
    <span className={`text-[10px] font-medium border rounded px-1.5 py-0.5 inline-flex items-center gap-1 ${STATUS_BADGE[status] || ""}`}>
      {status === "approved" && <CheckCircle2 className="h-3 w-3" />}
      {status === "rejected" && <XCircle className="h-3 w-3" />}
      {status === "cancelled" && <XCircle className="h-3 w-3" />}
      {status === "pending" && <Clock className="h-3 w-3" />}
      {status}
    </span>
  );
}

export default function AdminOptionalLeaves() {
  const [tab, setTab] = useState("inbox");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dialog, setDialog] = useState(null); // { action, row }
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nudgeDialog, setNudgeDialog] = useState(false);
  const [nudging, setNudging] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/optional-leaves"),
        api.get("/optional-leaves/stats", { params: { year } }),
      ]);
      setRows(a.data || []);
      setStats(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year]);

  const counts = useMemo(
    () => rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {}),
    [rows]
  );

  const filteredHistory = useMemo(
    () => rows.filter((r) => Number(r.year) === Number(year)),
    [rows, year]
  );

  const decide = async () => {
    if (!dialog) return;
    setSubmitting(true);
    try {
      await api.post(`/optional-leaves/${dialog.row.id}/${dialog.action}`, { remarks });
      toast.success(`Application ${dialog.action}d`);
      setDialog(null);
      setRemarks("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const sendNudge = async () => {
    setNudging(true);
    try {
      const ids = (stats?.utilization?.zero_usage_eligible || []).map((e) => e.id);
      const { data } = await api.post("/optional-leaves/nudge", {
        employee_ids: ids, year,
      });
      const parts = [];
      if (data.sent) parts.push(`${data.sent} nudge email(s) sent`);
      if (data.skipped_today) parts.push(`${data.skipped_today} skipped (already nudged today)`);
      if (!data.sent && !data.skipped_today) parts.push("No emails sent");
      toast.success(parts.join(" · "));
      setNudgeDialog(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setNudging(false);
    }
  };

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  return (
    <div className="space-y-5" data-testid="admin-optional-leaves">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">HR · Optional Leaves</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Optional Leave Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Approve requests, track utilization, and review year-on-year trends.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-500">Year</span>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[110px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="oleave-tabs">
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Inbox className="h-3.5 w-3.5 mr-1.5" />
            Inbox <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 rounded px-1.5">{counts.pending || 0}</span>
          </TabsTrigger>
          <TabsTrigger value="utilization" data-testid="tab-utilization">
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Utilization
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-3.5 w-3.5 mr-1.5" /> History & YoY
          </TabsTrigger>
        </TabsList>

        {/* ============ Inbox ============ */}
        <TabsContent value="inbox" className="space-y-3 mt-4" data-testid="panel-inbox">
          {loading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : (rows.filter((r) => r.status === "pending").length === 0) ? (
            <div className="bg-white border border-red-100 rounded-xl p-10 text-center" data-testid="inbox-empty">
              <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <div className="text-sm text-slate-600 font-medium">No pending requests</div>
              <div className="text-xs text-slate-400 mt-1">All optional-leave applications are decided.</div>
            </div>
          ) : (
            <div className="bg-white border border-red-100 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="inbox-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Holiday</th>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Reason</th>
                    <th className="text-left px-4 py-2">Applied</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.filter((r) => r.status === "pending").map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`pending-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.employee_name}</div>
                        <div className="text-[11px] text-slate-500">{r.punch_id || "—"}</div>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">
                        <Sparkles className="inline h-3 w-3 text-amber-500 mr-1" />
                        {r.holiday_name}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(r.holiday_date)}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[320px] whitespace-normal break-words align-top" title={r.reason}>
                        {r.reason || "—"}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-slate-500">{fmtDateTime(r.created_at)}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => setDialog({ action: "approve", row: r })}
                                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`approve-${r.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDialog({ action: "reject", row: r })}
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                data-testid={`reject-${r.id}`}>
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ============ Utilization ============ */}
        <TabsContent value="utilization" className="space-y-4 mt-4" data-testid="panel-utilization">
          {!stats ? (
            <div className="text-sm text-slate-500">Loading stats...</div>
          ) : (
            <>
              {/* Status summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-cards">
                <StatusSummaryCard label="Pending" value={stats.by_status.pending} color="amber" />
                <StatusSummaryCard label="Approved" value={stats.by_status.approved} color="emerald" />
                <StatusSummaryCard label="Rejected" value={stats.by_status.rejected} color="red" />
                <StatusSummaryCard label="Cancelled" value={stats.by_status.cancelled} color="slate" />
              </div>

              {/* Utilization buckets */}
              <section className="bg-white rounded-xl border border-red-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 brand-text" />
                    <h2 className="font-semibold text-slate-900">Employee Utilization · {stats.year}</h2>
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Total eligible: <b className="text-slate-900">{stats.utilization.total_employees}</b>
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3" data-testid="util-buckets">
                  <BucketCard label="Used 0 of 2" value={stats.utilization.buckets.zero} total={stats.utilization.total_employees} tone="slate" />
                  <BucketCard label="Used 1 of 2" value={stats.utilization.buckets.one} total={stats.utilization.total_employees} tone="amber" />
                  <BucketCard label="Used 2 of 2" value={stats.utilization.buckets.two} total={stats.utilization.total_employees} tone="emerald" />
                </div>
              </section>

              {/* Holiday popularity */}
              <section className="bg-white rounded-xl border border-red-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-amber-600" />
                  <h2 className="font-semibold text-slate-900">Holiday Popularity · {stats.year} (Approved)</h2>
                </div>
                {(stats.holiday_popularity || []).length === 0 ? (
                  <div className="text-xs text-slate-500 italic py-2">No optional leaves approved yet.</div>
                ) : (
                  <div className="space-y-2" data-testid="popularity-list">
                    {stats.holiday_popularity.map((h) => {
                      const max = Math.max(...stats.holiday_popularity.map((x) => x.count), 1);
                      const pct = Math.round((h.count / max) * 100);
                      return (
                        <div key={h.holiday_id} className="grid grid-cols-12 gap-2 items-center text-sm">
                          <div className="col-span-3 truncate font-medium text-slate-800">{h.name}</div>
                          <div className="col-span-2 text-[11px] text-slate-500">{fmtDate(h.date)}</div>
                          <div className="col-span-6">
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full brand-bg" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                          <div className="col-span-1 text-right text-sm font-semibold brand-text">{h.count}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* By department */}
              <section className="bg-white rounded-xl border border-red-100 p-5">
                <h2 className="font-semibold text-slate-900 mb-3">By Department · {stats.year}</h2>
                {(stats.by_department || []).length === 0 ? (
                  <div className="text-xs text-slate-500 italic py-2">No data.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="dept-table">
                    <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left py-2">Department</th>
                        <th className="text-right py-2">Eligible</th>
                        <th className="text-right py-2">Optional Leaves Used</th>
                        <th className="text-right py-2">Avg / Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_department.map((d) => (
                        <tr key={d.department} className="border-t border-slate-100">
                          <td className="py-2 font-medium">{d.department}</td>
                          <td className="py-2 text-right">{d.total}</td>
                          <td className="py-2 text-right font-semibold brand-text">{d.used}</td>
                          <td className="py-2 text-right text-slate-500">
                            {d.total ? (d.used / d.total).toFixed(2) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {/* Zero usage list */}
              <section className="bg-white rounded-xl border border-red-100 p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <h2 className="font-semibold text-slate-900">
                      Employees who haven&apos;t used any optional leave · {stats.year}
                    </h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-slate-500">
                      {stats.utilization.zero_usage_eligible.length} eligible (probation completed)
                    </span>
                    {stats.utilization.zero_usage_eligible.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setNudgeDialog(true)}
                        className="brand-bg brand-bg-hover text-white"
                        data-testid="send-nudge-btn"
                      >
                        <Mail className="h-3.5 w-3.5 mr-1.5" /> Send Nudge Email
                      </Button>
                    )}
                  </div>
                </div>
                {stats.utilization.zero_usage_eligible.length === 0 ? (
                  <div className="text-xs text-slate-500 italic py-2">Everyone has used at least one optional leave 🎉</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="zero-usage-grid">
                    {stats.utilization.zero_usage_eligible.map((e) => (
                      <div key={e.id} className="bg-amber-50/40 border border-amber-100 rounded px-3 py-2 text-sm">
                        <div className="font-medium text-slate-800">{e.name}</div>
                        <div className="text-[11px] text-slate-500">{e.punch_id} · {e.department}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>

        {/* ============ History & YoY ============ */}
        <TabsContent value="history" className="space-y-4 mt-4" data-testid="panel-history">
          {/* Year-on-year */}
          <section className="bg-white rounded-xl border border-red-100 p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 brand-text" /> Year-on-Year Trend
            </h2>
            <table className="w-full text-sm" data-testid="yoy-table">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2">Year</th>
                  <th className="text-right py-2">Applied</th>
                  <th className="text-right py-2">Approved</th>
                  <th className="text-right py-2">Rejected</th>
                  <th className="text-right py-2">Cancelled</th>
                  <th className="text-right py-2">Pending</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.yoy || []).map((y) => (
                  <tr key={y.year} className="border-t border-slate-100" data-testid={`yoy-row-${y.year}`}>
                    <td className="py-2 font-semibold">{y.year}</td>
                    <td className="py-2 text-right">{y.applied}</td>
                    <td className="py-2 text-right text-emerald-700 font-medium">{y.approved}</td>
                    <td className="py-2 text-right text-red-600">{y.rejected}</td>
                    <td className="py-2 text-right text-slate-500">{y.cancelled}</td>
                    <td className="py-2 text-right text-amber-700">{y.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* All-history table for the selected year */}
          <section className="bg-white rounded-xl border border-red-100 overflow-hidden">
            <div className="px-5 py-3 bg-red-50/50 border-b border-red-100">
              <h2 className="font-semibold text-slate-900">All Applications · {year}</h2>
            </div>
            {filteredHistory.length === 0 ? (
              <div className="text-xs text-slate-500 italic p-5">No applications for {year}.</div>
            ) : (
              <table className="w-full text-sm" data-testid="history-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Holiday</th>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Decided By</th>
                    <th className="text-left px-4 py-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`history-row-${r.id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{r.employee_name}</div>
                        <div className="text-[11px] text-slate-500">{r.punch_id || "—"}</div>
                      </td>
                      <td className="px-4 py-2">{r.holiday_name}</td>
                      <td className="px-4 py-2 text-slate-600">{fmtDate(r.holiday_date)}</td>
                      <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-2 text-slate-600">{r.decision_by_name || "—"}</td>
                      <td className="px-4 py-2 text-slate-600 max-w-[320px] whitespace-normal break-words align-top" title={r.decision_remarks}>
                        {r.decision_remarks || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </TabsContent>
      </Tabs>

      {/* Decision dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>        <DialogContent data-testid="decision-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {dialog.action === "approve" ? "Approve" : "Reject"} Optional Leave
                </DialogTitle>
                <DialogDescription>
                  <span className="block font-medium text-slate-900">{dialog.row.employee_name} ({dialog.row.punch_id})</span>
                  <span className="block text-slate-500">
                    {dialog.row.holiday_name} · {fmtDate(dialog.row.holiday_date)}
                  </span>
                  <span className="block text-xs mt-1 italic">&ldquo;{dialog.row.reason}&rdquo;</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  Remarks (optional)
                </label>
                <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                          placeholder={dialog.action === "approve" ? "Approved — enjoy!" : "Reason for rejection..."}
                          data-testid="decision-remarks-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} disabled={submitting}>Cancel</Button>
                <Button onClick={decide} disabled={submitting}
                        className={dialog.action === "approve"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-red-600 hover:bg-red-700 text-white"}
                        data-testid="decision-confirm-btn">
                  {submitting ? "Submitting..." : (dialog.action === "approve" ? "Confirm Approve" : "Confirm Reject")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Nudge confirm dialog */}
      <Dialog open={nudgeDialog} onOpenChange={(o) => !o && !nudging && setNudgeDialog(false)}>
        <DialogContent data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle>Send Nudge to Zero-Usage Employees</DialogTitle>
            <DialogDescription>
              A friendly reminder email will be sent to <b>{stats?.utilization?.zero_usage_eligible?.length || 0}</b> employee(s)
              who have not used any optional leave in <b>{year}</b> yet (probation completed).
              <br />
              <span className="text-[11px] text-slate-500 mt-2 block">
                Idempotent — employees already nudged today will be silently skipped.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50/60 border border-amber-100 rounded p-3 text-[12px] text-amber-900">
            <b>Recipients:</b>{" "}
            {(stats?.utilization?.zero_usage_eligible || []).slice(0, 4).map((e) => e.name).join(", ")}
            {(stats?.utilization?.zero_usage_eligible || []).length > 4
              ? `, and ${(stats?.utilization?.zero_usage_eligible || []).length - 4} more`
              : ""}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNudgeDialog(false)} disabled={nudging}>Cancel</Button>
            <Button onClick={sendNudge} disabled={nudging}
                    className="brand-bg brand-bg-hover text-white"
                    data-testid="nudge-confirm-btn">
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {nudging ? "Sending..." : "Send Nudge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusSummaryCard({ label, value, color }) {
  const map = {
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    red: "bg-red-50 border-red-100 text-red-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${map[color]}`}>
      <div className="text-2xl font-bold">{value || 0}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

function BucketCard({ label, value, total, tone }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const toneMap = {
    slate: "border-slate-200 text-slate-700",
    amber: "border-amber-200 text-amber-700",
    emerald: "border-emerald-200 text-emerald-700",
  };
  const fillMap = {
    slate: "bg-slate-400",
    amber: "bg-amber-500",
    emerald: "bg-emerald-500",
  };
  return (
    <div className={`rounded-xl border ${toneMap[tone]} bg-white p-4`}>
      <div className="flex items-baseline justify-between">
        <div className="text-3xl font-bold">{value}</div>
        <div className="text-sm opacity-70">{pct}%</div>
      </div>
      <div className="text-[11px] uppercase tracking-wider mt-1">{label}</div>
      <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
        <div className={`h-full ${fillMap[tone]}`} style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
}
